import SuperJSON from "superjson";

import { Decimal } from "~/utils/types/decimal";

/**
 * Custom SuperJSON serializers for tRPC data transfer.
 *
 * ## Decimal (Custom Handler)
 * Prisma Decimal fields are serialized as strings and deserialized back to Decimal objects.
 * This custom handler is registered below.
 *
 * ## BigInt (Built-in - DO NOT add custom handler)
 * SuperJSON has BUILT-IN BigInt support (serializes to string, deserializes back to bigint).
 * Do NOT register a custom bigint handler - it would conflict with the built-in one.
 *
 * ### Working with BigInt monetary values on the client:
 *
 * **For display:** Use `formatCurrency()` or `<CurrencyDisplay />` - both handle bigint natively.
 *
 * **For arithmetic:** Convert to `number` via `Number()`. This is safe for monetary display
 * since `Number.MAX_SAFE_INTEGER` (~9 quadrillion) far exceeds any realistic monetary amount
 * (~$90 trillion in cents). However, be aware:
 * - Use `Math.round()` after percentage calculations to avoid floating-point precision errors
 * - When writing back to Prisma BigInt columns, wrap results with `BigInt(Math.round(...))`
 *
 * **For BigInt sums:** Use `BigInt(0)` as the initial accumulator to preserve bigint type:
 * ```ts
 * items.reduce((acc, item) => acc + item.totalAmount, BigInt(0))
 * ```
 *
 * @see prisma/BIGINT_MIGRATION_INITIATIVE.md for full migration details and patterns
 */
SuperJSON.registerCustom<Decimal, string>(
  {
    isApplicable: (v): v is Decimal => Decimal.isDecimal(v),
    serialize: v => v.toJSON(),
    deserialize: v => new Decimal(v),
  },
  "decimal.js",
);

export default SuperJSON;
